import { Chart } from '@/models/chart.model'
import { AvailableWidgets, DashboardResource, WidgetProperties } from '@/models/dashboard.model'
import { IDashboardService } from '@/services/IDashboardService'
import { defineStore } from 'pinia'
import labels from '@/translations/labels.json'
import { SnaphotReference } from '@/models'

export interface DashboardState {
  dashboard: DashboardResource
  dashboardModified: boolean
}

export interface DashboardStoreState {
  selectedDashboardId: number
  availableWidgets: AvailableWidgets
  dashboardStates: DashboardState[]
}

export interface MetricUpdateData {
  metricId: string
  newX?: number
  newY?: number
  newWidth?: number
  newHeight?: number
}

function calculateGridPositionY(widgets: WidgetProperties[]): number {
  let absoluteWidth = 0
  widgets.forEach((widget) => {
    absoluteWidth += widget.width
  })
  if (absoluteWidth == 0) {
    return 0
  }
  absoluteWidth += 15
  return Math.floor(absoluteWidth / 36)
}

function calculateGridPositionX(widgets: WidgetProperties[]): number {
  const highestRow = getCurrentHighestRow(widgets)
  for (let index = 0; index <= highestRow; index++) {
    const absoluteWidthForRow = getAbsoluteWidthForRow(widgets, index)
    if (absoluteWidthForRow + 15 < 36) {
      return absoluteWidthForRow
    }
  }
  return 0
}

function getAbsoluteWidthForRow(widgets: WidgetProperties[], row: number): number {
  let absoluteWidth = 0
  widgets.forEach((widget) => {
    if (widget.y == row) {
      absoluteWidth += widget.width
    }
  })
  return absoluteWidth
}

function getCurrentHighestRow(widgets: WidgetProperties[]): number {
  let highestRow = 0
  widgets.forEach((widgets) => {
    if (widgets.y > highestRow) {
      highestRow = widgets.y
    }
  })
  return highestRow
}

export const useDashboardStore = defineStore('dashboardStore', {
  state: (): DashboardStoreState => ({
    selectedDashboardId: 0,
    availableWidgets: {
      widgets: [],
    },
    dashboardStates: [],
  }),
  getters: {
    getDashboardStateById:
      (state: DashboardStoreState) =>
      (id: number): DashboardState | undefined =>
        state.dashboardStates.find((dashboardState) => dashboardState.dashboard.id === id),

    selectedDashboardState(state: DashboardStoreState): DashboardState | undefined {
      return this.getDashboardStateById(state.selectedDashboardId)
    },
  },
  actions: {
    /**
     * API actions
     */
    async initializeDashboards(args: { dashboardService: IDashboardService; projectId: number }) {
      if (this.dashboardStates.length <= 0) {
        await this.fetchDashboards(args)
      }
    },

    async fetchDashboards({
      dashboardService,
      projectId,
    }: {
      dashboardService: IDashboardService
      projectId: number
    }) {
      const dashboards = await dashboardService.getDashboards(projectId)

      this.dashboardStates = dashboards.map((dashboard) => ({
        dashboard,
        dashboardModified: false,
      }))
    },

    async addNewDashboard(
      dashboardService: IDashboardService,
      projectId: number,
    ): Promise<DashboardState> {
      const dashboard = await dashboardService.createDashboard(projectId, {
        name: labels.DASHBOARD.DEFAULT_DASHBOARD_TITLE,
        description: labels.DASHBOARD.DEFAULT_DASHBOARD_DESCRIPTION,
        widgets: [],
      })

      const newDashboardState: DashboardState = {
        dashboard,
        dashboardModified: false,
      }

      this.dashboardStates.push(newDashboardState)

      return newDashboardState
    },

    async updateDashboard(
      dashboardService: IDashboardService,
      projectId: number,
      dashboard: DashboardResource,
    ) {
      const updated = await dashboardService.updateDashboard(projectId, dashboard)

      const charts = this.availableWidgets.widgets.flatMap((widget) => widget.charts)

      updated.widgets.forEach((w) => {
        w.chart = charts.find((c) => c.id === w.widgetId)
      })

      const newDashboardState = { dashboard: updated, dashboardModified: false }

      this.dashboardStates.forEach((state, index) => {
        if (state.dashboard.id === updated.id) {
          this.dashboardStates.splice(index, 1, newDashboardState)
        }
      })

      return newDashboardState
    },

    async removeDashboard(
      dashboardService: IDashboardService,
      projectId: number,
      dashboard: DashboardResource,
    ) {
      await dashboardService.deleteDashboard(projectId, dashboard.id)

      this.dashboardStates = this.dashboardStates.filter((s) => s.dashboard.id !== dashboard.id)
    },

    async loadAvailableWidgetsFromApi(params: {
      dashboardService: IDashboardService
      projectId: number
      snapshotId: SnaphotReference
      filter?: number
    }): Promise<void> {
      const widgets = await params.dashboardService.getAvailableWidgets(
        params.projectId,
        params.snapshotId,
        params.filter,
      )

      this.availableWidgets = widgets

      // Add charts to widget properties where the id matches
      this.dashboardStates.forEach((state) => {
        state.dashboard.widgets.forEach((widget: WidgetProperties) => {
          widget.chart = widgets.widgets
            .flatMap((w) => w.charts)
            .find((c) => c.id === widget.widgetId)
        })
      })
    },

    async loadDataForChartFromApi(params: {
      dashboardService: IDashboardService
      chart: Chart
      force?: boolean
    }) {
      if (params.chart.data === undefined || params.force) {
        const data = await params.dashboardService.getChartData(params.chart)

        this.selectedDashboardState?.dashboard.widgets
          .flatMap((widget) => widget.chart)
          .filter((chart): chart is Chart => chart !== undefined)
          .filter((chart) => chart.path === params.chart.path)
          .forEach((chart) => (chart.data = data))
      }
    },

    /**
     * Other actions
     */
    markDashboardModified(): void {
      const dashboardState = this.selectedDashboardState
      if (dashboardState) {
        dashboardState.dashboardModified = true
      }
    },
    markDashboardSaved(): void {
      const dashboardState = this.selectedDashboardState

      if (dashboardState) {
        dashboardState.dashboardModified = false
      }
    },

    addWidgetFromChart(chart: Chart): void {
      const existingWidgets = this.selectedDashboardState?.dashboard.widgets ?? []

      this.selectedDashboardState?.dashboard.widgets.push({
        // Assign a random id, backend will assign a new one anyway.
        // The ID is necessary so the widget can be updated and deleted before it is saved to the backend.
        id: Math.floor(Math.random() * 1_000_000_000),
        widgetId: chart.id,
        title: chart.defaultTitle,
        chartType: chart.chartType,
        configuration: {},
        x: calculateGridPositionX(existingWidgets),
        y: calculateGridPositionY(existingWidgets),
        width: 15,
        height: 10,
        hiddenSeries: [],
        // Add the chart from which this widget properties object was created
        chart: chart,
      })

      this.markDashboardModified()
    },

    updateWidgetProperties(
      widgetId: number | undefined,
      updated: Partial<
        Pick<
          WidgetProperties,
          'title' | 'chartType' | 'hiddenSeries' | 'configuration' | 'width' | 'height' | 'x' | 'y'
        >
      >,
    ) {
      const widget = this.selectedDashboardState?.dashboard.widgets.find((w) => w.id === widgetId)
      if (widget) {
        widget.title = updated.title ?? widget.title
        widget.chartType = updated.chartType ?? widget.chartType
        widget.hiddenSeries = updated.hiddenSeries ?? widget.hiddenSeries
        widget.configuration = updated.configuration ?? widget.configuration
        widget.width = updated.width ?? widget.width
        widget.height = updated.height ?? widget.height
        widget.x = updated.x ?? widget.x
        widget.y = updated.y ?? widget.y

        this.markDashboardModified()
      }
    },

    removeWidget(widget: WidgetProperties): void {
      const dashboardState = this.selectedDashboardState

      if (dashboardState) {
        dashboardState.dashboard.widgets.forEach((w, index) => {
          if (widget.id === w.id) dashboardState.dashboard.widgets.splice(index, 1)
        })

        this.markDashboardModified()
      }
    },
  },
})
